import React from 'react';

const SectionSplitter = ({ theme, variant = 'glow', className = '' }) => {
  const isDark = theme === 'dark';

  // Modern gradient divider with animated shimmer
  if (variant === 'wave') {
    return (
      <div className={`relative overflow-hidden ${className}`} style={{ height: 80, margin: '0' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 12,
        }}>
          {/* Main wave line */}
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none"
            style={{ width: '100%', height: 40, display: 'block' }} aria-hidden="true">
            <defs>
              <linearGradient id="wave-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(220,38,38,0)" />
                <stop offset="20%" stopColor={isDark ? 'rgba(220,38,38,0.4)' : 'rgba(220,38,38,0.25)'} />
                <stop offset="50%" stopColor={isDark ? 'rgba(220,38,38,0.8)' : 'rgba(220,38,38,0.5)'} />
                <stop offset="80%" stopColor={isDark ? 'rgba(220,38,38,0.4)' : 'rgba(220,38,38,0.25)'} />
                <stop offset="100%" stopColor="rgba(220,38,38,0)" />
              </linearGradient>
              <filter id="wave-glow">
                <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <path
              d="M0,20 C180,5 360,35 540,20 C720,5 900,35 1080,20 C1260,5 1380,30 1440,20"
              stroke="url(#wave-grad)"
              strokeWidth="1.5"
              fill="none"
              filter="url(#wave-glow)"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (variant === 'glow') {
    return (
      <div className={`relative flex items-center justify-center py-10 overflow-hidden ${className}`}>
        {/* Glowing horizontal rule */}
        <div style={{
          position: 'absolute',
          left: '5%', right: '5%', top: '50%',
          height: 1,
          background: isDark
            ? 'linear-gradient(90deg, transparent, rgba(220,38,38,0.6) 30%, rgba(220,38,38,0.9) 50%, rgba(220,38,38,0.6) 70%, transparent)'
            : 'linear-gradient(90deg, transparent, rgba(220,38,38,0.35) 30%, rgba(220,38,38,0.6) 50%, rgba(220,38,38,0.35) 70%, transparent)',
          transform: 'translateY(-50%)',
          boxShadow: isDark ? '0 0 12px rgba(220,38,38,0.4)' : 'none',
        }} />
        {/* Center badge */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 20px',
          borderRadius: 999,
          border: isDark ? '1.5px solid rgba(220,38,38,0.25)' : '1.5px solid rgba(220,38,38,0.18)',
          background: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: isDark ? '0 0 24px rgba(220,38,38,0.15)' : '0 2px 16px rgba(0,0,0,0.06)',
        }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              width: i === 1 ? 8 : 5,
              height: i === 1 ? 8 : 5,
              borderRadius: '50%',
              background: i === 1
                ? 'linear-gradient(135deg, #dc2626, #991b1b)'
                : `rgba(220,38,38,${i === 0 ? 0.4 : 0.4})`,
              boxShadow: i === 1 ? '0 0 8px rgba(220,38,38,0.6)' : 'none',
              animation: `breathPulseGlobal ${2.2 + i * 0.4}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
              display: 'inline-block',
            }} />
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'diagonal') {
    return (
      <div className={`relative overflow-hidden ${className}`} style={{ height: 60 }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none"
          style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
          <defs>
            <linearGradient id="diag-fill" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(220,38,38,0)" />
              <stop offset="30%" stopColor={isDark ? 'rgba(220,38,38,0.06)' : 'rgba(220,38,38,0.04)'} />
              <stop offset="70%" stopColor={isDark ? 'rgba(220,38,38,0.1)' : 'rgba(220,38,38,0.06)'} />
              <stop offset="100%" stopColor="rgba(220,38,38,0)" />
            </linearGradient>
            <linearGradient id="diag-line" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(220,38,38,0)" />
              <stop offset="25%" stopColor={isDark ? 'rgba(220,38,38,0.5)' : 'rgba(220,38,38,0.3)'} />
              <stop offset="75%" stopColor={isDark ? 'rgba(220,38,38,0.5)' : 'rgba(220,38,38,0.3)'} />
              <stop offset="100%" stopColor="rgba(220,38,38,0)" />
            </linearGradient>
          </defs>
          <path d="M0,40 L1440,20 L1440,60 L0,60 Z" fill="url(#diag-fill)" />
          <path d="M0,40 L1440,20" stroke="url(#diag-line)" strokeWidth="1" fill="none" />
        </svg>
      </div>
    );
  }

  if (variant === 'curve') {
    return (
      <div className={`relative overflow-hidden ${className}`} style={{ height: 64 }}>
        <svg viewBox="0 0 1440 64" preserveAspectRatio="none"
          style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
          <defs>
            <linearGradient id="curve-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(220,38,38,0)" />
              <stop offset="20%" stopColor={isDark ? 'rgba(220,38,38,0.35)' : 'rgba(220,38,38,0.2)'} />
              <stop offset="50%" stopColor={isDark ? 'rgba(220,38,38,0.7)' : 'rgba(220,38,38,0.4)'} />
              <stop offset="80%" stopColor={isDark ? 'rgba(220,38,38,0.35)' : 'rgba(220,38,38,0.2)'} />
              <stop offset="100%" stopColor="rgba(220,38,38,0)" />
            </linearGradient>
          </defs>
          <path
            d="M0,32 Q360,0 720,32 Q1080,64 1440,32"
            stroke="url(#curve-grad)"
            strokeWidth="1.5"
            fill="none"
          />
          <path
            d="M0,38 Q360,6 720,38 Q1080,70 1440,38"
            stroke={isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.08)'}
            strokeWidth="1"
            fill="none"
          />
        </svg>
      </div>
    );
  }

  if (variant === 'slant') {
    return (
      <div className={`relative overflow-hidden ${className}`} style={{ height: 48 }}>
        <div style={{
          position: 'absolute',
          left: 0, right: 0,
          top: '50%',
          transform: 'translateY(-50%) skewY(-1.5deg)',
          height: 1,
          background: isDark
            ? 'linear-gradient(90deg, transparent, rgba(220,38,38,0.5) 25%, rgba(220,38,38,0.8) 50%, rgba(220,38,38,0.5) 75%, transparent)'
            : 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3) 25%, rgba(220,38,38,0.5) 50%, rgba(220,38,38,0.3) 75%, transparent)',
        }} />
      </div>
    );
  }

  if (variant === 'dots') {
    return (
      <div className={`flex items-center justify-center gap-3 py-8 ${className}`}>
        <div style={{
          flex: 1,
          height: 1,
          background: isDark
            ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06))'
            : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.08))',
        }} />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[0,1,2,3,4].map(i => (
            <span key={i} style={{
              display: 'inline-block',
              width: i === 2 ? 10 : i === 1 || i === 3 ? 6 : 4,
              height: i === 2 ? 10 : i === 1 || i === 3 ? 6 : 4,
              borderRadius: '50%',
              background: `rgba(220,38,38,${i === 2 ? 0.8 : i === 1 || i === 3 ? 0.5 : 0.3})`,
              boxShadow: i === 2 ? '0 0 10px rgba(220,38,38,0.5)' : 'none',
              animation: `breathPulseGlobal ${2 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${Math.abs(i - 2) * 0.2}s`,
            }} />
          ))}
        </div>
        <div style={{
          flex: 1,
          height: 1,
          background: isDark
            ? 'linear-gradient(90deg, rgba(255,255,255,0.06), transparent)'
            : 'linear-gradient(90deg, rgba(0,0,0,0.08), transparent)',
        }} />
      </div>
    );
  }

  // Default minimal
  return (
    <div className={`relative flex items-center gap-4 py-8 px-4 ${className}`}>
      <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)' }} />
      <div style={{ display: 'flex', gap: 5 }}>
        {[0,1,2].map(i => (
          <span key={i} style={{
            display: 'inline-block',
            width: i === 1 ? 6 : 4,
            height: i === 1 ? 6 : 4,
            borderRadius: '50%',
            background: `rgba(220,38,38,${i === 1 ? 0.6 : 0.3})`,
          }} />
        ))}
      </div>
      <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)' }} />
    </div>
  );
};

export default SectionSplitter;
